import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type DiscountTrigger = 'AUTOMATIC' | 'CODE';
export type DiscountActionType =
  | 'PERCENTAGE'
  | 'FIXED_AMOUNT'
  | 'FIXED_PRICE'
  | 'FREE_SHIPPING'
  | 'BXGY';
export type DiscountTargetScope = 'CART' | 'MATCHED_ITEMS';
export type DiscountPeriod = 'ALL_TIME' | 'MONTH' | 'SEASON';

export interface DiscountConditions {
  cartSubtotalMinCents?: number;
  cartQtyMin?: number;
  categories?: string[];
  productIds?: string[];
  skus?: string[];
  targetMinQty?: number;
  firstPurchase?: boolean;
  fulfillmentMethod?: 'IN_STORE_PICKUP' | 'SHIPPING';
}

export interface RuleTierDto {
  id: string;
  tierId: string;
  maxRedemptionsPerUser: number | null;
  period: DiscountPeriod | null;
}

export interface DiscountRuleDto {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  trigger: DiscountTrigger;
  code: string | null;
  startsAt: string | null;
  endsAt: string | null;
  conditions: DiscountConditions;
  actionType: DiscountActionType;
  actionValue: string | null;
  actionMaxDiscount: string | null;
  targetScope: DiscountTargetScope;
  bxgyBuyQty: number | null;
  bxgyGetQty: number | null;
  priority: number;
  stackable: boolean;
  exclusive: boolean;
  maxRedemptionsTotal: number | null;
  maxRedemptionsPerUser: number | null;
  perUserPeriod: DiscountPeriod;
  createdAt: string;
  tiers: RuleTierDto[];
}

export interface CreateDiscountRuleInput {
  name: string;
  description?: string;
  trigger?: DiscountTrigger;
  code?: string;
  startsAt?: string;
  endsAt?: string;
  conditions?: DiscountConditions;
  actionType: DiscountActionType;
  actionValue?: number;
  actionMaxDiscount?: number;
  targetScope?: DiscountTargetScope;
  bxgyBuyQty?: number;
  bxgyGetQty?: number;
  priority?: number;
  stackable?: boolean;
  exclusive?: boolean;
  maxRedemptionsTotal?: number;
  maxRedemptionsPerUser?: number;
  perUserPeriod?: DiscountPeriod;
  isActive?: boolean;
}

export type UpdateDiscountRuleInput = Partial<Omit<CreateDiscountRuleInput, 'code' | 'trigger'>>;

export interface FanTierDto {
  id: string;
  key: string;
  name: Record<string, string>;
  rank: number;
  minPoints: number;
  minSubscriptionType: string | null;
  color: string | null;
  isActive: boolean;
}

export interface CreateFanTierInput {
  key: string;
  name: Record<string, string>;
  rank: number;
  minPoints: number;
  minSubscriptionType?: string;
  color?: string;
  isActive?: boolean;
}

export type SkipReason =
  | 'TIER_NOT_ENTITLED'
  | 'LIMIT_TOTAL_REACHED'
  | 'LIMIT_USER_REACHED'
  | 'NOT_ACTIVE'
  | 'OUT_OF_WINDOW'
  | 'CODE_NOT_PROVIDED'
  | 'CONDITIONS_NOT_MET'
  | 'NO_MATCHING_ITEMS'
  | 'NOT_STACKABLE'
  | 'STOPPED_BY_EXCLUSIVE'
  | 'ZERO_AMOUNT';

export interface SimulationResult {
  applied: Array<{
    ruleId: string;
    ruleName: string;
    ruleCode: string | null;
    amountCents: number;
    appliedTo: 'ITEMS' | 'SHIPPING';
  }>;
  skipped: Array<{ ruleId: string; ruleName: string; reason: SkipReason }>;
  subtotalCents: number;
  itemsDiscountCents: number;
  shippingDiscountCents: number;
  totalCents: number;
}

export interface SimulateInput {
  items: Array<{ productId: string; quantity: number }>;
  tierId?: string;
  firstPurchase?: boolean;
  fulfillmentMethod?: 'IN_STORE_PICKUP' | 'SHIPPING';
  shippingCents?: number;
  couponCode?: string;
}

export interface RedemptionDto {
  id: string;
  userId: string;
  orderId: string;
  amount: string;
  periodKey: string;
  voidedAt: string | null;
  createdAt: string;
}

const QK = {
  rules: ['discount-rules'] as const,
  rule: (id: string) => ['discount-rules', id] as const,
  tiers: ['fan-tiers'] as const,
  redemptions: (id: string) => ['discount-rules', id, 'redemptions'] as const,
};

// ── regole ──────────────────────────────────────────────────────────────────

export function useDiscountRules() {
  return useQuery({
    queryKey: QK.rules,
    queryFn: async () => {
      const res = await apiClient.get<DiscountRuleDto[]>('/shop/discount-rules');
      return res.data;
    },
  });
}

export function useCreateDiscountRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDiscountRuleInput) => {
      const res = await apiClient.post<DiscountRuleDto>('/shop/discount-rules', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.rules }),
  });
}

export function useUpdateDiscountRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateDiscountRuleInput }) => {
      const res = await apiClient.patch<DiscountRuleDto>(`/shop/discount-rules/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.rules }),
  });
}

export function useSetDiscountRuleActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await apiClient.patch<DiscountRuleDto>(`/shop/discount-rules/${id}/active`, {
        isActive,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.rules }),
  });
}

export function useDeleteDiscountRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete<{ deleted: boolean }>(`/shop/discount-rules/${id}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.rules }),
  });
}

export function useSetRuleTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      tiers,
    }: {
      id: string;
      tiers: Array<{ tierId: string; maxRedemptionsPerUser?: number; period?: DiscountPeriod }>;
    }) => {
      const res = await apiClient.put<DiscountRuleDto>(`/shop/discount-rules/${id}/tiers`, {
        tiers,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.rules }),
  });
}

export function useRuleRedemptions(id: string | null) {
  return useQuery({
    queryKey: QK.redemptions(id ?? 'none'),
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await apiClient.get<{ total: number; items: RedemptionDto[] }>(
        `/shop/discount-rules/${id}/redemptions`,
      );
      return res.data;
    },
  });
}

/** Il simulatore non ha cache: ogni prova e' una domanda nuova. */
export function useSimulateDiscounts() {
  return useMutation({
    mutationFn: async (input: SimulateInput) => {
      const res = await apiClient.post<SimulationResult>('/shop/discount-rules/simulate', input);
      return res.data;
    },
  });
}

// ── tier ────────────────────────────────────────────────────────────────────

export function useFanTiers() {
  return useQuery({
    queryKey: QK.tiers,
    queryFn: async () => {
      const res = await apiClient.get<FanTierDto[]>('/shop/fan-tiers');
      return res.data;
    },
  });
}

export function useCreateFanTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFanTierInput) => {
      const res = await apiClient.post<FanTierDto>('/shop/fan-tiers', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.tiers }),
  });
}

export function useUpdateFanTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<CreateFanTierInput> }) => {
      const res = await apiClient.patch<FanTierDto>(`/shop/fan-tiers/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.tiers }),
  });
}

export function useDeleteFanTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/shop/fan-tiers/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.tiers }),
  });
}
